<template>
  <Card
    class="grid grid-cols-[3rem_1fr_2.5rem_4rem] sm:grid-cols-[4rem_1fr_4rem_8rem] items-center !p-4 gap-x-4 rounded-md bg-white"
  >
    <div
      @click="showInfo = true"
      class="cursor-pointer hover:drama-2 drama-nami-comi-blue transition-all"
    >
      <AsyncImage
        v-if="achievement"
        :src="getAchievementImage(achievement)"
        :aspect="1"
      />
      <div
        v-else
        class="stage-gradient-nami rounded-md w-full h-full flex items-center justify-center text-white aspect-square"
      >
        <IconAward :size="60" :stroke-width="1.5" />
      </div>
    </div>
    <div
      class="min-w-0 my-auto"
      :class="{
        'col-span-2': !badge || hideBadge,
      }"
    >
      <h3
        class="font-medium truncate sm:text-lg sm:mb-1 flex items-center space-x-1"
      >
        <span class="min-w-0 truncate">
          {{
            !hideDetails && achievement
              ? extractFromLocale(locale, achievement.attributes.name)
              : t("components.AchievementProgressCard.title.hidden")
          }}
        </span>
        <IconInfoCircle
          v-if="hideDetails"
          class="sm:hidden flex-shrink-0"
          @click="showInfo = true"
        />
        <slot name="afterName"></slot>
      </h3>
      <i18n-t
        v-if="!hideDetails && achievement"
        class="truncate text-xs sm:text-base"
        :keypath="`components.AchievementProgressCard.unlock_description.${achievement.attributes.metric}`"
        :plural="total"
        tag="p"
      >
        <template #count>{{ total }}</template>
        <template #organization>
          <TheNuxtLink
            v-if="relatedOrg"
            :href="linkTo(relatedOrg)"
            class="text-nami-comi-blue hover:underline"
          >
            {{ relatedOrg?.attributes?.name ?? "" }}
          </TheNuxtLink>
          <span v-else class="italic">unknown</span>
        </template>
      </i18n-t>
      <p v-else class="italic">
        {{ t("components.AchievementProgressCard.unlock_description.hidden") }}
      </p>
    </div>
    <div
      v-if="badge && !hideBadge"
      class="flex items-center justify-center text-center rounded-md min-w-0 p-2 border relative overflow-hidden bg-neutral-100 border-neutral-300 dark:bg-neutral-800 dark:border-neutral-700"
    >
      <AsyncImage
        :src="getBadgeImage(badge)"
        :aspect="1"
        :class="{
          disabled: progress.attributes.targetReached && !hideAwardIndicator,
        }"
      />
      <div
        v-if="progress.attributes.targetReached && !hideAwardIndicator"
        class="absolute inset-0 flex items-center justify-center text-green-600 dark:text-green-400 bg-green-400/20 dark:bg-green-700/20"
      >
        <IconCheck :stroke-width="4" :size="36" class="scale-75 sm:scale-100" />
      </div>
    </div>
    <div
      class="flex flex-col items-center justify-center text-center font-medium text-xs sm:text-base"
    >
      <span v-if="progress.attributes.reachedAt">{{
        dayjs(progress.attributes.reachedAt).format("MMM YYYY")
      }}</span>
      <span v-else-if="isCheckbox || hideDetails">{{
        t("components.AchievementProgressCard.state.in_progress")
      }}</span>
      <span v-else>{{ progressLabel }}</span>
    </div>
    <NamiModal v-model="showInfo">
      <NamiModalTitle @close="showInfo = false">{{
        t("components.AchievementProgressCard.modal.title")
      }}</NamiModalTitle>
      <div class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <div v-if="achievement" class="col-span-2 grid grid-cols-3 mb-3">
          <AsyncImage
            class="col-start-2"
            :src="getAchievementImage(achievement)"
          />
        </div>
        <span class="font-medium">{{
          t("components.AchievementProgressCard.modal.label.source")
        }}</span>
        <OrganizationCardDense
          v-if="relatedOrg"
          class="px-2 bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors"
          :org="relatedOrg"
        />
        <span v-else>{{
          t("components.AchievementProgressCard.modal.source.global")
        }}</span>
        <span class="font-medium">{{
          t("components.AchievementProgressCard.modal.label.name")
        }}</span>
        <h3>
          {{
            !hideDetails && achievement
              ? extractFromLocale(locale, achievement.attributes.name)
              : t("components.AchievementProgressCard.title.hidden")
          }}
        </h3>
        <span class="font-medium">{{
          t("components.AchievementProgressCard.modal.label.description")
        }}</span>
        <i18n-t
          v-if="!hideDetails && achievement"
          :keypath="`components.AchievementProgressCard.unlock_description.${achievement.attributes.metric}`"
          :plural="total"
          tag="p"
        >
          <template #count>{{ total }}</template>
          <template #organization>
            <TheNuxtLink
              v-if="relatedOrg"
              :href="linkTo(relatedOrg)"
              class="text-nami-comi-blue hover:underline"
            >
              {{ relatedOrg?.attributes?.name ?? "" }}
            </TheNuxtLink>
            <span v-else class="italic">unknown</span>
          </template>
        </i18n-t>
        <span class="font-medium">{{
          t("components.AchievementProgressCard.modal.label.status")
        }}</span>
        <span v-if="progress.attributes.reachedAt">{{
          t("components.AchievementProgressCard.modal.progress.completed", {
            date: dayjs(progress.attributes.reachedAt).format("LLL"),
          })
        }}</span>
        <span v-else-if="isCheckbox || hideDetails">{{
          t("components.AchievementProgressCard.modal.progress.in_progress")
        }}</span>
        <span v-else>{{ progressLabel }}</span>
        <span class="font-medium">{{
          t("components.AchievementProgressCard.modal.label.reward")
        }}</span>
        <div v-if="badge">
          <Badge :badge="badge" size="fill" class="w-8 h-8" />
        </div>
        <span v-else>{{
          t("components.AchievementProgressCard.modal.reward.none")
        }}</span>
      </div>
    </NamiModal>
  </Card>
</template>

<script setup lang="ts">
import {
  type AchievementEntity,
  type AchievementProgressEntity,
  getAchievementImage,
  getBadgeImage,
  isAchievement,
  isBadge,
  isExpanded,
  isOrg,
} from "~/src/api";
import { hasAgeParameter, hasCountParameter } from "~/src/forms/achievement";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { IconCheck, IconAward, IconInfoCircle } from "@tabler/icons-vue";

dayjs.extend(LocalizedFormat);

const nuxtApp = useNuxtApp();
const { locale, t } = useI18n();

const showInfo = ref(false);

const { progress, achievement: inAchievement } = defineProps<{
  progress: AchievementProgressEntity;
  achievement?: AchievementEntity;
  hideBadge?: boolean;
  hideAwardIndicator?: boolean;
}>();

// we dont know, its hidden and not obtained
const hideDetails = computed(
  () =>
    !achievement.value ||
    (achievement.value.attributes.isHidden && !progress.attributes.reachedAt),
);

const achievement = computed(() => {
  const found = inAchievement || progress.relationships.find(isAchievement);
  if (!found || !isExpanded(found)) return null;
  return found;
});
const badge = computed(() => {
  const found = progress.relationships.find(isBadge);
  if (!found || !isExpanded(found)) return null;
  return found;
});
const relatedOrg = computed(() => {
  const found = inAchievement?.relationships.find(isOrg);
  if (!found || !isExpanded(found)) return null;
  return found;
});

const secondsDay = 60 * 60 * 24;

const isCheckbox = computed(
  () =>
    !achievement.value ||
    (!hasAgeParameter(achievement.value.attributes.metric) &&
      !hasCountParameter(achievement.value.attributes.metric)),
);

const currentProgress = computed(() => {
  if (!achievement.value)
    return progress.attributes.reachedAt
      ? progress.attributes.targetValue
      : progress.attributes.progressValue;
  if (hasAgeParameter(achievement.value.attributes.metric)) {
    const targetDays =
      achievement.value.attributes.parameters!.ageSeconds! / secondsDay;
    const currTotalDays = Math.min(
      (
        (progress.attributes.targetValue - progress.attributes.progressValue) /
        secondsDay
      ).round(),
      targetDays,
    );
    if (currTotalDays < 0) {
      return targetDays + currTotalDays;
    }
    return currTotalDays;
  }
  if (hasCountParameter(achievement.value.attributes.metric))
    return progress.attributes.progressValue;
  return progress.attributes.progressValue;
});

const total = computed(() => {
  if (!achievement.value) return progress.attributes.targetValue;
  if (hasAgeParameter(achievement.value.attributes.metric))
    return achievement.value.attributes.parameters!.ageSeconds! / secondsDay;
  if (hasCountParameter(achievement.value.attributes.metric))
    return progress.attributes.targetValue;

  return progress.attributes.targetValue;
});

const progressLabel = computed(() => {
  if (progress.attributes.targetReached) return "completed";
  if (currentProgress.value < total.value)
    return `${currentProgress.value}/${total.value}`;
  return "completed";
});
</script>
